// Pure, dependency-free view-ownership rule shared by BOTH the server (savedTableView router
// enforcement) and the client (TableViewSelector/ViewPill UI gating), so the rule lives in one
// place. The server is the authoritative gate; the client mirror is UX only (disable, don't
// hide). Keep free of React / DB / env imports so it stays bundle-safe for both sides.

import type { FilterInput } from "~/server/api/utils/filterSchema"

/**
 * A principal may modify a view only when they own it, or it is shared and they have edit
 * permission. Another user's personal view is off-limits to EVERYONE — including Hakio staff
 * (PRO-1212): there is deliberately no staff bypass here. Staff retain shared-view editing
 * through `canEdit`, which `permissionsForRoles` already grants them.
 */
export function canModifyView(
  permissions: { canEdit: boolean },
  ownership: { isOwn: boolean; isShared: boolean },
): boolean {
  return ownership.isOwn || (ownership.isShared && permissions.canEdit)
}

export type ViewScope = "personal" | "shared" | "other"

type View = { userId?: string | null } | null | undefined

/** Ownership of a saved view, resolved from the owner id both sides store. An unknown
 * `viewerId` owns nothing. */
export function ownershipForView(
  view: View,
  viewerId: string | null,
): { isOwn: boolean; isShared: boolean } {
  const ownerId = view?.userId ?? null
  return {
    isOwn: !!view && ownerId !== null && ownerId === viewerId,
    isShared: !!view && ownerId === null,
  }
}

/** The viewer id a client resolves a view against. Auth0 clears `user` during a silent token
 * renewal without unmounting the app, so the viewer is `undefined` while the app stays
 * interactive; falling back to the view's own owner costs at worst a control the server
 * rejects, where being strict costs an owner the use of their own view. */
function clientViewerId(view: View, viewerId: string | undefined): string | null {
  return viewerId ?? view?.userId ?? null
}

/** The scope label for a saved view, resolved through the same ownership rule as the gate. */
export function resolveViewScope(view: View, viewerId: string | undefined): ViewScope {
  const { isOwn, isShared } = ownershipForView(view, clientViewerId(view, viewerId))
  if (isShared) return "shared"
  return isOwn ? "personal" : "other"
}

/** `canModifyView` for a saved view: the client-side entry point to the server's rule. */
export function canModifySavedView(
  permissions: { canEdit: boolean },
  view: View,
  viewerId: string | undefined,
): boolean {
  return canModifyView(permissions, ownershipForView(view, clientViewerId(view, viewerId)))
}

/**
 * Whether a principal may see a view group (and the shared views inside it).
 * Access is a UNION: an unrestricted group is visible to everyone; a restricted
 * group is visible to its directly granted users and to members of any granted
 * team. `memberUserIds` is the flattened membership of every granted AccessGroup,
 * resolved by the caller. Staff/admin bypasses live at the call sites, not here.
 */
export function canAccessViewGroup(
  userId: string,
  group: { restricted: boolean; userIds: string[]; memberUserIds: string[] },
): boolean {
  if (!group.restricted) return true
  return group.userIds.includes(userId) || group.memberUserIds.includes(userId)
}

/** A saved filter without its gate and group: locked filters are a flat AND. */
export type LockedFilter = Omit<FilterInput, "gate" | "groupId">

/** Operators that pin a field to a set of values and can therefore be OR'ed by merging. */
const EQUALITY_OPERATORS = new Set(["=", "oneOf"])

function equalityValues(filter: LockedFilter): string[] {
  return filter.operator === "oneOf" ? filter.values : [filter.value]
}

/**
 * Combine the locked filters of every team a user belongs to into one flat filter list.
 * A user in both the US and the EU team must see both regions, so equality filters on the
 * same field are OR'ed by merging their values into one "oneOf". Every other filter is kept
 * as-is and AND'ed, duplicates collapsed. Gates and groups an admin may have drawn inside one
 * team's list are dropped: locked filters are a flat AND on top of the page's own filters.
 */
export function mergeTeamLockedFilters(filterSets: LockedFilter[][]): LockedFilter[] {
  const equalityByField = new Map<string, LockedFilter>()
  const others = new Map<string, LockedFilter>()

  for (const filters of filterSets) {
    for (const raw of filters) {
      const filter: LockedFilter = {
        field: raw.field,
        operator: raw.operator,
        value: raw.value,
        by: raw.by,
        values: raw.values,
      }
      if (!EQUALITY_OPERATORS.has(filter.operator)) {
        others.set(JSON.stringify(filter), filter)
        continue
      }
      const key = `${filter.field}\u0000${filter.by}`
      const existing = equalityByField.get(key)
      if (!existing) {
        equalityByField.set(key, filter)
        continue
      }
      const union = [...new Set([...equalityValues(existing), ...equalityValues(filter)])]
      const unchanged = union.length === equalityValues(existing).length
      if (unchanged) continue
      equalityByField.set(key, {
        field: filter.field,
        by: filter.by,
        operator: "oneOf",
        value: "",
        values: union,
      })
    }
  }

  return [...equalityByField.values(), ...others.values()]
}
